import React, { createContext, useContext, useState, useMemo, useEffect, useCallback, ReactNode } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { DateRange } from "react-day-picker";
import { ReviewFilterOrderOptions, ReviewFilterSortOptions, ReviewSentiment } from "@/services/bedrock";
import { resolveReviewDatePreset, type ReviewSourceFilterValue } from "@/components/reviews";

interface ReviewFiltersContextType {
  search: string;
  setSearch: (val: string) => void;
  triageFilter: "needingReply" | "replied" | "skipped" | null;
  setTriageFilter: (val: "needingReply" | "replied" | "skipped" | null) => void;
  sourceSystemFilter: ReviewSourceFilterValue | null;
  setSourceSystemFilter: (val: ReviewSourceFilterValue | null) => void;
  ratingTypeFilter: string | null;
  setRatingTypeFilter: (val: string | null) => void;
  preset: string | null;
  setPreset: (val: string | null) => void;
  date: DateRange | undefined;
  setDate: (val: DateRange | undefined) => void;
  sortFilter: ReviewFilterSortOptions | null;
  setSortFilter: (val: ReviewFilterSortOptions | null) => void;
  orderFilter: ReviewFilterOrderOptions | null;
  setOrderFilter: (val: ReviewFilterOrderOptions | null) => void;
  sentimentFilter: ReviewSentiment | null;
  setSentimentFilter: (val: ReviewSentiment | null) => void;
  clearFilters: () => void;
  activeFiltersCount: number;
  completedReviews: Record<string | number, boolean>;
  markReviewAsCompleted: (reviewId: string | number) => void;
  dismissCompletedReview: (reviewId: string | number) => void;
}

const ReviewFiltersContext = createContext<ReviewFiltersContextType | undefined>(undefined);

export function ReviewFiltersProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const history = useHistory();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [search, setSearch] = useState("");
  const [triageFilter, setTriageFilterState] = useState<"needingReply" | "replied" | "skipped" | null>(null);

  // Custom Filters (null means cleared / "All")
  const [sourceSystemFilter, setSourceSystemFilter] = useState<any>(null);
  const [ratingTypeFilter, setRatingTypeFilter] = useState<any>(null);
  const [preset, setPreset] = useState<string | null>(null);
  const [date, setDate] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });

  // Sorting and Sentiment
  const [sortFilter, setSortFilter] = useState<any>("effective_date");
  const [orderFilter, setOrderFilter] = useState<any>("desc");
  const [sentimentFilter, setSentimentFilter] = useState<any>(null);

  const [completedReviews, setCompletedReviews] = useState<Record<string | number, boolean>>({});

  const markReviewAsCompleted = useCallback((reviewId: string | number) => {
    setCompletedReviews(prev => ({ ...prev, [reviewId]: true }));
  }, []);

  const dismissCompletedReview = useCallback((reviewId: string | number) => {
    setCompletedReviews(prev => {
      const copy = { ...prev };
      delete copy[reviewId];
      return copy;
    });
  }, []);

  const activeFiltersCount =
    (triageFilter ? 1 : 0) +
    (sourceSystemFilter ? 1 : 0) +
    (ratingTypeFilter ? 1 : 0) +
    (preset && preset !== "all_time" ? 1 : 0) +
    (sentimentFilter ? 1 : 0) +
    (sortFilter && sortFilter !== "effective_date" ? 1 : 0) +
    (orderFilter && orderFilter !== "desc" ? 1 : 0);

  // Mount-only: apply ?needingReply=true once. Don't depend on queryParams — that undoes clear on remount.
  useEffect(() => {
    const isNeedingReply = queryParams.get("needingReply") === "true";
    if (isNeedingReply) {
      setTriageFilterState("needingReply");
      setPreset("last_30_days");
      const { startDate, endDate } = resolveReviewDatePreset("last_30_days");
      setDate({
        from: startDate ? new Date(`${startDate}T00:00:00`) : undefined,
        to: endDate ? new Date(`${endDate}T23:59:59`) : undefined,
      });
    }
  }, []);

  const setTriageFilter = useCallback(
    (val: "needingReply" | "replied" | "skipped" | null) => {
      setTriageFilterState(val);
      history.push({ search: val === "needingReply" ? "?needingReply=true" : "" });
    },
    [history]
  );

  const clearFilters = useCallback(() => {
    setTriageFilterState(null);
    setSourceSystemFilter(null);
    setRatingTypeFilter(null);
    setPreset(null);
    setSortFilter("effective_date");
    setOrderFilter("desc");
    setSentimentFilter(null);
    setDate({
      from: undefined,
      to: undefined,
    });
    history.push({ search: "" });
  }, [history]);

  const value = useMemo(
    () => ({
      search,
      setSearch,
      triageFilter,
      setTriageFilter,
      sourceSystemFilter,
      setSourceSystemFilter,
      ratingTypeFilter,
      setRatingTypeFilter,
      preset,
      setPreset,
      date,
      setDate,
      sortFilter,
      setSortFilter,
      orderFilter,
      setOrderFilter,
      sentimentFilter,
      setSentimentFilter,
      clearFilters,
      activeFiltersCount,
      completedReviews,
      markReviewAsCompleted,
      dismissCompletedReview,
    }),
    [
      search,
      triageFilter,
      setTriageFilter,
      sourceSystemFilter,
      ratingTypeFilter,
      preset,
      date,
      sortFilter,
      orderFilter,
      sentimentFilter,
      clearFilters,
      activeFiltersCount,
      completedReviews,
      markReviewAsCompleted,
      dismissCompletedReview,
    ]
  );

  return <ReviewFiltersContext.Provider value={value}>{children}</ReviewFiltersContext.Provider>;
}

export function useReviewFilters() {
  const context = useContext(ReviewFiltersContext);
  if (context === undefined) {
    throw new Error("useReviewFilters must be used within a ReviewFiltersProvider");
  }
  return context;
}
